import {
  SolflareWalletName,
  SolflareWalletAdapter,
  SolflareWalletAdapterConfig,
  PhantomWalletName,
  PhantomWalletAdapter,
  PhantomWalletAdapterConfig,
  BackpackWalletName,
  BackpackWalletAdapter,
} from "@solana/wallet-adapter-wallets";
import {
  WalletAdapter,
  WalletAdapterNetwork,
  WalletNotConnectedError,
  WalletNotReadyError,
  WalletReadyState,
} from "@solana/wallet-adapter-base";
import type {
  Adapter,
  MessageSignerWalletAdapter,
  MessageSignerWalletAdapterProps,
  SendTransactionOptions,
  SignerWalletAdapter,
  SignerWalletAdapterProps,
  WalletError,
  WalletName,
} from "@solana/wallet-adapter-base";
import { Transaction } from "@solana/web3.js";
import type {
  Connection,
  PublicKey,
  TransactionSignature,
  VersionedTransaction,
} from "@solana/web3.js";

declare global {
  interface Window {
    walletsAlert: any;
    walletsLoad: any;
    walletsDetected: any;
    walletsConnect: any;
    walletsDisconnect: any;
    walletsSign: any;
    walletConnected: any;
    walletConnectedPubKey: string;
    walletConnectEventElement: any;
    walletConnectEventElementName: string;
  }
}

let walletConnectConnectedWallet: WalletAdapter | undefined;

let walletConnectWallets: WalletAdapter[] = [];

function onReadyStateChange(this: Adapter, readyState: WalletReadyState) {
  console.log("onReadyStateChange", readyState);
  if (!this) {
    console.log("no adapter");
    return;
  }

  console.log("adapter", this);
  console.log("readyState", readyState);
  establishEmitter();
  if (window.walletConnectEventElement) {
    window.walletConnectEventElement.dispatchEvent(
      new CustomEvent("walletReadyStateChange", {
        detail: { name: this.name, ready_state: readyState.toString() },
      }),
    );
  }
}

function onConnect(this: Adapter) {
  if (!this) {
    console.log("no adapter");
    return;
  }

  console.log("adapter", this);
  console.log("connected", this.connected);
  console.log("publicKey", this.publicKey?.toString());
  if (this.connected) {
    walletConnectConnectedWallet = this;
    window.walletConnectedPubKey = this.publicKey?.toString() || "";
  }

  establishEmitter();
  if (window.walletConnectEventElement) {
    window.walletConnectEventElement.dispatchEvent(
      new CustomEvent("walletConnect", {
        detail: { public_key: this.publicKey?.toString() },
      }),
    );
  }
}

function onAccountChanged(this: Adapter, newPublicKey: PublicKey | null) {
  console.log("adapter", this);
  console.log("publicKey", newPublicKey?.toString());
  if (newPublicKey) {
    window.walletConnectedPubKey = newPublicKey?.toString() || "";
  }

  establishEmitter();
  if (window.walletConnectEventElement) {
    window.walletConnectEventElement.dispatchEvent(
      new CustomEvent("walletAccountChange", {
        detail: { public_key: newPublicKey?.toString() },
      }),
    );
  }
}

function onDisconnect(this: Adapter) {
  console.log("adapter", this);
  console.log("disconnected");
  walletConnectConnectedWallet = undefined;
  window.walletConnectedPubKey = "";

  establishEmitter();
  if (window.walletConnectEventElement) {
    window.walletConnectEventElement.dispatchEvent(
      new CustomEvent("walletDisconnected", {
        detail: { public_key: "" },
      }),
    );
  }
}

function onError(error: WalletError) {
  console.error(error);
  establishEmitter();
  if (window.walletConnectEventElement) {
    window.walletConnectEventElement.dispatchEvent(
      new CustomEvent("walletError", {
        detail: { error: error.toString() },
      }),
    );
  }
}

window.walletConnected = () => window.walletConnectedPubKey || "";

window.walletsAlert = (message: string) => {
  window.alert(message);
};

window.walletsDetected = (): { name: string; icon: string }[] => {
  let walletsDetected = walletConnectWallets
    .filter((adapter) => adapter.readyState == WalletReadyState.Installed)
    .map((adapter) => {
      return { name: adapter.name, icon: adapter.icon };
    });

  return walletsDetected;
};

window.walletsConnect = async (
  walletName: WalletName,
): Promise<string | undefined> => {
  const adapter = walletConnectWallets.find(
    (adapter) => adapter.name === walletName,
  );
  if (!adapter) {
    throw new Error(`Wallet not found: ${walletName}`);
  }

  console.log(`adapter ${adapter.name} in state ${adapter.readyState}`);
  if (adapter.readyState == WalletReadyState.Installed) {
    console.log(`adapter ${adapter.name} is installed. connecting...`);
    await adapter.connect();
    return adapter.publicKey.toString();
  } else {
    console.log(`adapter ${adapter.name} is not installed.`);
  }

  return;
};

window.walletsDisconnect = async (): Promise<void> => {
  if (walletConnectConnectedWallet) {
    console.log("disconnecting wallet");
    await walletConnectConnectedWallet.disconnect();
  }
};

let establishEmitter = () => {
  if (
    window.walletConnectEventElementName &&
    document.getElementById(window.walletConnectEventElementName)
  ) {
    window.walletConnectEventElement = document.getElementById(
      window.walletConnectEventElementName,
    );
    return window.walletConnectEventElement;
  }
};

window.walletsLoad = (eventElement: string): string[] => {
  // WalletConnect code
  window.walletConnectEventElement = document.getElementById(eventElement);
  window.walletConnectEventElementName = eventElement;

  console.log(`${SolflareWalletName} wallet adapter`);
  console.log(`${PhantomWalletName} wallet adapter`);
  console.log(`${BackpackWalletName} wallet adapter`);

  const network = WalletAdapterNetwork.Mainnet;
  // const network = WalletAdapterNetwork.Devnet;
  walletConnectWallets = [
    new SolflareWalletAdapter({ network }),
    new BackpackWalletAdapter({ network }),
    new PhantomWalletAdapter({ network }),
  ];

  walletConnectWallets.forEach((adapter) => {
    adapter.on("readyStateChange", onReadyStateChange, adapter);
    adapter.on("connect", onConnect, adapter);
    adapter.on("disconnect", onDisconnect, adapter);
    adapter.on("accountChanged", onAccountChanged, adapter);
    adapter.on("error", onError);
  });

  return walletConnectWallets.map((adapter) => adapter.name);
  // adapter.on('disconnect', onDisconnect);
};

window.walletsSign = async (encodedTx: string): Promise<string | undefined> => {
  console.log(`encodedTx to sign: ${encodedTx}`);
  if (walletConnectConnectedWallet) {
    const tx = Transaction.from(Buffer.from(encodedTx, "base64"));

    if ("signTransaction" in walletConnectConnectedWallet) {
      const signedTx = await (
        walletConnectConnectedWallet as SignerWalletAdapter
      ).signTransaction(tx);
      const serialized = signedTx.serialize().toString("base64");
      console.log(`signature: ${serialized}`);
      return serialized;
    }

    throw new Error(
      `Wallet ${walletConnectConnectedWallet.name} does not support signing transactions`,
    );
  }
};
